import {Controller} from "stimulus"

// Adds a backing only when artwork needs contrast; existing consumers retain
// their brightness heuristic. DIOS opts into conservative white-only detection:
// <div data-controller="logo-background" data-logo-background-white-only-value="true"
//      data-logo-background-background-color-value="#BBC7FF">
//   <img crossorigin="anonymous" data-logo-background-target="image"
//        data-action="error->logo-background#restoreImage" src="...">
// </div>
export default class extends Controller {
  static targets = ['image']
  static values = {
    backgroundColor: String,
    whiteOnly: Boolean
  }

  connect() {
    this.analyzed = false
    // Use IntersectionObserver to analyze only visible images
    this.setupIntersectionObserver()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  get backgroundColor() {
    return this.hasBackgroundColorValue ? this.backgroundColorValue : '#000000'
  }

  setupIntersectionObserver() {
    if (!this.hasImageTarget) return

    // Configure observer with margin to pre-load before it is visible
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.analyzed) {
          this.analyzed = true
          this.checkImageBrightness()
          this.observer.disconnect()
        }
      })
    }, {
      rootMargin: '50px',
      threshold: 0.01
    })

    this.observer.observe(this.element)
  }

  checkImageBrightness() {
    if (!this.hasImageTarget) return

    const img = this.imageTarget
    if (img.complete && img.naturalWidth === 0) {
      this.restoreImage()
      return
    }

    if (!img.complete) {
      img.addEventListener('load', () => this.analyzeImage(img), {once: true})
      return
    }
    this.analyzeImage(img)
  }

  analyzeImage(img) {
    if (this.corsFailed || img.naturalWidth === 0) return

    const analyze = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d', {willReadFrequently: true})

        const sampleSize = 50
        canvas.width = sampleSize
        canvas.height = sampleSize

        ctx.drawImage(img, 0, 0, sampleSize, sampleSize)

        const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize)
        const data = imageData.data

        if (this.whiteOnlyValue) {
          if (this.isWhiteArtwork(data)) this.applyBackground()
          return
        }

        let brightPixels = 0
        let totalPixels = 0
        let transparentPixels = 0

        for (let i = 0; i < data.length; i += 8) {
          const a = data[i + 3]

          if (a < 128) {
            transparentPixels++
            continue
          }

          totalPixels++

          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3

          if (brightness > 230) {
            brightPixels++
          }
        }

        const brightRatio = totalPixels > 0 ? brightPixels / totalPixels : 0
        const transparentRatio = (transparentPixels + totalPixels) > 0
          ? transparentPixels / (transparentPixels + totalPixels)
          : 0

        // Require more light pixels (70%) to activate the background
        if (brightRatio > 0.7 && transparentRatio > 0.3) {
          this.applyBackground()
        }

      } catch (error) {
        if (error.name === 'SecurityError') {
          // Sometimes Brandfetch does, according to my testing. When it allows, use crossorigin: 'anonymous' to avoid errors.
          console.warn('CORS blocked - cannot analyze:', img.src)
        } else {
          console.error('analyzeImage error:', error, 'for:', img.src)
        }
      }
    }

    // The bounded white-only scan runs at load, before the next paint, rather
    // than leaving an invisible logo waiting up to two seconds for idle time.
    if (this.whiteOnlyValue) {
      analyze()
    } else if ('requestIdleCallback' in window) {
      requestIdleCallback(analyze, {timeout: 2000})
    } else {
      setTimeout(analyze, 0)
    }
  }

  // Ignore transparent RGB and weight antialiased edges by opacity. Requiring
  // transparency excludes opaque white tiles; checking every channel excludes
  // bright yellow/pastel marks that an average-brightness test would misclassify.
  isWhiteArtwork(data) {
    let visibleWeight = 0
    let whiteWeight = 0
    let hasTransparency = false

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3]
      if (alpha < 255) hasTransparency = true
      if (alpha < 16) continue

      visibleWeight += alpha
      if (data[i] >= 240 && data[i + 1] >= 240 && data[i + 2] >= 240) {
        whiteWeight += alpha
      }
    }

    return hasTransparency && visibleWeight > 0 && whiteWeight / visibleWeight >= 0.98
  }

  // Some logo hosts disallow CORS. Keep those images visible without analysis,
  // retrying normal image loading once instead of breaking previously good logos.
  restoreImage() {
    const img = this.imageTarget
    if (!img.hasAttribute('crossorigin')) return

    this.corsFailed = true
    img.removeAttribute('crossorigin')
    img.src = img.src
  }

  applyBackground() {
    this.element.style.backgroundColor = this.backgroundColor
    this.element.classList.add('has-logo-background')
  }
}